import { LanguageSwitcher } from "@/components/Common/LanguageSwitcher"
import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { ColorModeButton, useColorModeValue } from "@/components/ui/color-mode"
import { Field } from "@/components/ui/field"
import { ReactIcon } from "@/components/ui/icon"
import { InputGroup } from "@/components/ui/input-group"
import { RouterLink } from "@/components/ui/router-link"
import { useDemoMode } from "@/hooks/useDemoMode"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { mintVisitorSession } from "@/lib/demoAuth"
import { effects } from "@/theme"
import {
  Box,
  Container,
  Flex,
  HStack,
  Heading,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa"
import { FiPlayCircle } from "react-icons/fi"
import { useBoolean } from "usehooks-ts"
import {
  type Body_login_login_access_token as AccessToken,
  OpenAPI,
} from "../client"
import { isLoggedIn } from "../hooks/useAuth"
import { emailPattern } from "../utils"

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

type LoginResult =
  | { kind: "token"; accessToken: string }
  | { kind: "mfa"; mfaToken: string }

// Password step. Returns either a session token or a 2SV challenge. Done with a
// raw fetch (not the generated client) because the response is now a union the
// generated Token type can't express.
async function passwordLogin(
  username: string,
  password: string,
): Promise<LoginResult> {
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  const res = await fetch(`${base}/api/v1/login/access-token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ username, password }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error((data?.detail as string) || "Incorrect email or password")
  }
  if (data?.mfa_required && data?.mfa_token) {
    return { kind: "mfa", mfaToken: data.mfa_token as string }
  }
  return { kind: "token", accessToken: data.access_token as string }
}

// 2SV step. Exchanges the challenge token + code for a real session token.
async function verifyTwoSv(mfaToken: string, code: string): Promise<string> {
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  const res = await fetch(`${base}/api/v1/login/2sv-verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mfa_token: mfaToken, code }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error((data?.detail as string) || "Incorrect verification code")
  }
  return data.access_token as string
}

function Login() {
  const { t } = useTranslation("auth")
  const { t: tDemo } = useTranslation("demo")
  const { value: show, toggle } = useBoolean()
  const [error, setError] = useState<string | null>(null)
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [verifying, setVerifying] = useState(false)
  const { enabled: demoEnabled, bannerData } = useDemoMode()
  const navigate = useNavigate()
  const sc = useSemanticColors()

  // When demo mode is on, default to hiding the username/password form
  // and surface the visitor CTA instead. Operators that need to sign in
  // can expand the form via "Sign in as administrator instead".
  const [showAdminForm, setShowAdminForm] = useState(false)
  const adminFormVisible = !demoEnabled || showAdminForm
  const logoSrc = useColorModeValue("/qdat-logo-light.svg", "/qdat-logo.svg")
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccessToken>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const onSubmit: SubmitHandler<AccessToken> = async (data) => {
    if (isSubmitting) return
    setError(null)
    try {
      const result = await passwordLogin(data.username, data.password)
      if (result.kind === "mfa") {
        // Password OK but 2SV is enabled — switch to the code step.
        setMfaToken(result.mfaToken)
        return
      }
      localStorage.setItem("access_token", result.accessToken)
      navigate({ to: "/" })
    } catch (e) {
      setError(e instanceof Error ? e.message : "Login failed")
    }
  }

  const onVerify = async () => {
    if (verifying || !mfaToken) return
    setError(null)
    setVerifying(true)
    try {
      const token = await verifyTwoSv(mfaToken, code.trim())
      localStorage.setItem("access_token", token)
      navigate({ to: "/" })
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed")
    } finally {
      setVerifying(false)
    }
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={sc.pageBg}
      backgroundImage={effects.gradients.bgGradient}
      backgroundAttachment="fixed"
      p={{ base: 3, md: 4 }}
    >
      <HStack position="absolute" top={4} right={4} gap={2}>
        <LanguageSwitcher />
        <ColorModeButton />
      </HStack>
      <Container maxW="md" px={0}>
        <VStack gap={6}>
          <Image
            src={logoSrc}
            alt="QDAT"
            h={{ base: "40px", md: "50px" }}
            mb={2}
          />
          <GradientBorder borderRadius="30px" p={0}>
            <VisionCard
              variant="elevated"
              p={{ base: 5, md: 8 }}
              borderRadius="30px"
            >
              <VStack gap={6} w="100%">
                <Flex direction="column" align="center" gap={4}>
                  <IconBox variant="gradient" size="xl">
                    <FaLock fontSize="36px" />
                  </IconBox>
                  <Heading size="lg" color={sc.heading}>
                    {t("login.title")}
                  </Heading>
                  <Text color={sc.textMuted} textAlign="center">
                    {t("login.subtitle")}
                  </Text>
                </Flex>

                {/* Demo CTA — visible on top when demo mode is on so
                    visitors get straight to "Open the demo" instead of
                    being confronted with a username/password form they
                    can't use. Hidden during the 2SV code step. */}
                {!mfaToken && demoEnabled && (
                  <Box
                    w="100%"
                    p={4}
                    borderRadius="md"
                    bg="yellow.50"
                    _dark={{ bg: "yellow.900" }}
                    borderWidth="1px"
                    borderColor="yellow.300"
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      mb={1}
                      color={sc.heading}
                    >
                      {tDemo("login.tryDemoTitle", "Just exploring?")}
                    </Text>
                    <Text fontSize="xs" mb={3} color={sc.textMuted}>
                      {tDemo("login.tryDemoSubtitle", {
                        defaultValue:
                          "No signup required. Data resets every {{hours}}h.",
                        hours: bannerData?.purge_interval_hours ?? 4,
                      })}
                    </Text>
                    <VisionButton
                      type="button"
                      size="md"
                      w="100%"
                      variant="brand"
                      onClick={async () => {
                        try {
                          await mintVisitorSession()
                          navigate({ to: "/" })
                        } catch (e) {
                          console.error(e)
                        }
                      }}
                    >
                      <FiPlayCircle style={{ marginRight: 8 }} />
                      {tDemo("login.tryDemoCta", "Open the demo")}
                    </VisionButton>
                  </Box>
                )}

                {/* Admin sign-in form: shown by default in non-demo mode;
                    collapsed behind a toggle in demo mode so the visitor
                    CTA leads. */}
                {!mfaToken && demoEnabled && !showAdminForm && (
                  <Box w="100%" textAlign="center">
                    <VisionButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdminForm(true)}
                    >
                      {tDemo(
                        "login.adminSignInToggle",
                        "Sign in as administrator instead",
                      )}
                    </VisionButton>
                  </Box>
                )}

                {!mfaToken && adminFormVisible && (
                  <VStack
                    w="100%"
                    gap={4}
                    as="form"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {demoEnabled && (
                      <Box w="100%" textAlign="left">
                        <VisionButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAdminForm(false)}
                        >
                          {tDemo(
                            "login.adminSignInBack",
                            "← Back to demo access",
                          )}
                        </VisionButton>
                      </Box>
                    )}

                    <Field
                      invalid={!!errors.username || !!error}
                      errorText={errors.username?.message}
                    >
                      <VisionInput
                        {...register("username", {
                          required: "Email is required",
                          pattern: emailPattern,
                        })}
                        placeholder={t("fields.email")}
                        type="email"
                        size="lg"
                        variant="glass"
                        required
                      />
                    </Field>

                    <Field invalid={!!error} errorText={error}>
                      <InputGroup
                        width="100%"
                        endElement={
                          <ReactIcon
                            icon={show ? FaEyeSlash : FaEye}
                            cursor="pointer"
                            onClick={toggle}
                            aria-label={
                              show ? t("hidePassword") : t("showPassword")
                            }
                            color={sc.textMuted}
                            _hover={{ color: sc.heading }}
                          />
                        }
                      >
                        <VisionInput
                          {...register("password", {
                            required: "Password is required",
                          })}
                          type={show ? "text" : "password"}
                          placeholder={t("fields.password")}
                          size="lg"
                          variant="glass"
                          required
                        />
                      </InputGroup>
                    </Field>

                    <Box w="100%" textAlign="right">
                      <RouterLink
                        to="/recover-password"
                        color="brand.200"
                        fontSize="sm"
                      >
                        {t("login.forgotPassword")}
                      </RouterLink>
                    </Box>

                    <VisionButton
                      type="submit"
                      loading={isSubmitting}
                      size="lg"
                      w="100%"
                      variant="brand"
                    >
                      {t("login.submit")}
                    </VisionButton>

                    {!demoEnabled && (
                      <Text color={sc.textMuted} fontSize="sm">
                        {t("login.noAccount")}{" "}
                        <RouterLink
                          to="/signup"
                          color="brand.200"
                          fontWeight="semibold"
                        >
                          {t("login.signUp")}
                        </RouterLink>
                      </Text>
                    )}
                  </VStack>
                )}

                {/* 2SV code step — shown after a correct password when the
                    account has two-step verification enabled. */}
                {mfaToken && (
                  <VStack
                    w="100%"
                    gap={4}
                    as="form"
                    onSubmit={(e) => {
                      e.preventDefault()
                      onVerify()
                    }}
                  >
                    <Text color={sc.textMuted} fontSize="sm" textAlign="center">
                      {t(
                        "twoSv.prompt",
                        "Enter the 6-digit code from your authenticator app, or a recovery code.",
                      )}
                    </Text>
                    <Field invalid={!!error} errorText={error}>
                      <VisionInput
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={t("twoSv.codePlaceholder", "123456")}
                        size="lg"
                        variant="glass"
                        autoFocus
                        autoComplete="one-time-code"
                        inputMode="numeric"
                      />
                    </Field>
                    <VisionButton
                      type="submit"
                      loading={verifying}
                      size="lg"
                      w="100%"
                      variant="brand"
                    >
                      {t("twoSv.verify", "Verify")}
                    </VisionButton>
                    <VisionButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setMfaToken(null)
                        setCode("")
                        setError(null)
                      }}
                    >
                      {t("twoSv.back", "← Use a different account")}
                    </VisionButton>
                  </VStack>
                )}
              </VStack>
            </VisionCard>
          </GradientBorder>
        </VStack>
      </Container>
    </Flex>
  )
}
